import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveFamily } from '../context/ActiveFamilyContext';
import { GroupType, ContextCopys, CONTEXT_COPYS } from '../types/group';

/**
 * Helper: resolves all string values in a ContextCopys object through t()
 */
function translateCopys(
  raw: ContextCopys,
  t: (key: string) => string
): ContextCopys {
  const result = {} as any;
  for (const key of Object.keys(raw)) {
    const val = (raw as any)[key];
    // Emoji fields stay as-is (they don't need translation)
    if (key.endsWith('Emoji')) {
      result[key] = val;
    } else {
      result[key] = t(val);
    }
  }
  return result as ContextCopys;
}

/**
 * Hook para obtener los copys contextualizados según el tipo de grupo activo
 * 
 * @returns Los copys personalizados para el tipo de grupo actual
 * 
 * @example
 * ```tsx
 * const copys = useContextualCopys();
 * 
 * // En lugar de hardcodear "Miembros"
 * <h2>{copys.members}</h2>
 * 
 * // Muestra:
 * // - "Miembros" si es familia
 * // - "Estudiantes" si es grado
 * // - "Pareja" si es pareja
 * ```
 */
export function useContextualCopys(): ContextCopys {
  const { t } = useTranslation();
  const { activeFamily } = useActiveFamily();

  const copys = useMemo(() => {
    const groupType = (activeFamily?.groupType as GroupType) || 'familia';
    const raw = CONTEXT_COPYS[groupType] || CONTEXT_COPYS.familia;
    return translateCopys(raw, t);
  }, [activeFamily?.groupType, t]);

  return copys;
}

/**
 * Hook para obtener copys de un tipo de grupo específico
 * Útil cuando necesitas mostrar información de un grupo que no es el activo
 * 
 * @param groupType - El tipo de grupo del que obtener los copys
 * @returns Los copys personalizados para ese tipo de grupo
 */
export function useGroupTypeCopys(groupType: GroupType | undefined): ContextCopys {
  const { t } = useTranslation();

  const copys = useMemo(() => {
    const raw = groupType ? CONTEXT_COPYS[groupType] : CONTEXT_COPYS.familia;
    return translateCopys(raw || CONTEXT_COPYS.familia, t);
  }, [groupType, t]);

  return copys;
}
